import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import "chart.js/auto";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowUpRight,
  FilePlus2,
  Pencil,
  Smartphone,
  Trash2,
} from "lucide-react";
import "../styles/dashboard.css";
import WebsiteAnalyticsDashboard from "../components/WebsiteAnalyticsDashboard";
import { clearAuthToken, fetchMessages, getAuthToken } from "../utils/adminApi";
import { fetchArticleEngagementAnalytics } from "../utils/articleEngagementApi";
import { getCountryLabel } from "../utils/countryNames";
import { getDirectImageUrl } from "../utils/imageUrls";
import { fetchVisitorAnalytics } from "../utils/visitorTracking";

const ANDROID_APPS_ENDPOINT = API_ENDPOINTS.androidApps;

const APP_CATEGORIES = [
  "General",
  "Productivity",
  "Finance",
  "Transportation",
  "AI / ML",
  "Health & Fitness",
  "Education",
  "Utilities",
  "Entertainment",
  "Social",
  "Business",
  "Other",
];

const EMPTY_APP_FORM = {
  name: "",
  description: "",
  packageName: "",
  category: "General",
  status: "active",
  iconUrl: "",
  rating: "",
  playStoreLink: "",
  appStoreLink: "",
  githubLink: "",
  releasedAt: "",
  notes: "",
};

const TRAFFIC_RANGE_OPTIONS = [
  { value: "all", label: "All time", heading: "All time", days: null },
  { value: "90", label: "90 days", heading: "Last 90 days", days: 90 },
  { value: "60", label: "60 days", heading: "Last 60 days", days: 60 },
  { value: "30", label: "30 days", heading: "Last 30 days", days: 30 },
  { value: "14", label: "14 days", heading: "Last 14 days", days: 14 },
];

const DEFAULT_TRAFFIC_RANGE = "14";

const startOfDay = (value = new Date()) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const formatIsoDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseIsoDate = (value) => {
  const normalizedValue = (value || "").slice(0, 10);

  if (!normalizedValue) {
    return null;
  }

  const [year, month, day] = normalizedValue.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const formatTrendLabel = (date, includeYear = false) =>
  date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  });

const formatDateTime = (value) => {
  if (!value) {
    return "Unknown";
  }

  return new Date(value).toLocaleString();
};

const formatDuration = (value, zeroLabel = "0m") => {
  const totalSeconds = Math.max(0, Math.round((Number(value) || 0) / 1000));

  if (!totalSeconds) {
    return zeroLabel;
  }

  const units = [
    { label: "d", size: 86400 },
    { label: "h", size: 3600 },
    { label: "m", size: 60 },
    { label: "s", size: 1 },
  ];
  const parts = [];
  let remainingSeconds = totalSeconds;

  units.forEach((unit) => {
    if (!remainingSeconds) {
      return;
    }

    const amount = Math.floor(remainingSeconds / unit.size);

    if (!amount) {
      return;
    }

    parts.push(`${amount}${unit.label}`);
    remainingSeconds -= amount * unit.size;
  });

  return parts.slice(0, 2).join(" ");
};

const formatPagePath = (pageUrl) => {
  if (!pageUrl) {
    return "/";
  }

  try {
    return new URL(pageUrl).pathname || "/";
  } catch (error) {
    return pageUrl;
  }
};

const hasResolvedCity = (visitor) =>
  Boolean(visitor?.city && visitor.city !== "Unknown");

const getVisitorAcquisition = (visitor) =>
  visitor?.latestAcquisition || visitor?.firstAcquisition || {};

const truncateText = (value, maxLength = 140) => {
  if (!value) {
    return "";
  }

  return value.length > maxLength
    ? `${value.slice(0, maxLength).trim()}...`
    : value;
};

const getArticleDisplayLabel = (article) => {
  if (!article) {
    return "Untitled article";
  }

  return article.articleTitle?.trim() || article.slug || "Untitled article";
};

const getDailyWindow = (
  days = 14,
  endDate = new Date(),
  includeYear = false,
) => {
  const today = startOfDay(endDate);

  return Array.from({ length: days }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (days - 1 - index));

    return {
      iso: formatIsoDate(date),
      label: formatTrendLabel(date, includeYear),
      count: 0,
    };
  });
};

const buildMessageTrend = (contacts, days = 14) => {
  const buckets = getDailyWindow(days);
  const bucketMap = new Map(buckets.map((bucket) => [bucket.iso, bucket]));

  contacts.forEach((contact) => {
    const isoDate = new Date(contact.createdAt || contact.timestamp)
      .toISOString()
      .slice(0, 10);

    if (bucketMap.has(isoDate)) {
      bucketMap.get(isoDate).count += 1;
    }
  });

  return buckets;
};

const buildTrafficTrend = (
  dailyTraffic,
  selectedRange = DEFAULT_TRAFFIC_RANGE,
) => {
  const selectedOption =
    TRAFFIC_RANGE_OPTIONS.find((option) => option.value === selectedRange) ||
    TRAFFIC_RANGE_OPTIONS[TRAFFIC_RANGE_OPTIONS.length - 1];

  const today = startOfDay();
  const normalizedEntries = dailyTraffic
    .map((entry) => ({
      ...entry,
      date: (entry.date || "").slice(0, 10),
    }))
    .filter((entry) => entry.date);

  const earliestDate = normalizedEntries.reduce((earliest, entry) => {
    if (!earliest || entry.date < earliest) {
      return entry.date;
    }

    return earliest;
  }, "");

  const startDate = selectedOption.days
    ? (() => {
        const date = new Date(today);
        date.setDate(today.getDate() - (selectedOption.days - 1));
        return date;
      })()
    : parseIsoDate(earliestDate) || today;

  const totalDays = selectedOption.days
    ? selectedOption.days
    : Math.max(
        1,
        Math.floor((today.getTime() - startDate.getTime()) / 86400000) + 1,
      );

  const includeYear = startDate.getFullYear() !== today.getFullYear();

  const buckets = getDailyWindow(totalDays, today, includeYear).map(
    (bucket) => ({
      ...bucket,
      uniqueVisitors: 0,
      pageViews: 0,
    }),
  );

  const bucketMap = new Map(buckets.map((bucket) => [bucket.iso, bucket]));

  normalizedEntries.forEach((entry) => {
    if (bucketMap.has(entry.date)) {
      const bucket = bucketMap.get(entry.date);
      bucket.uniqueVisitors += Number(entry.uniqueVisitors) || 0;
      bucket.pageViews += Number(entry.pageViews) || 0;
    }
  });

  return buckets;
};

const groupMessagesBySubject = (contacts) => {
  const counts = contacts.reduce((accumulator, contact) => {
    const normalizedSubject = (contact.subject || "General inquiry").trim();
    accumulator[normalizedSubject] = (accumulator[normalizedSubject] || 0) + 1;
    return accumulator;
  }, {});

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([subject, count]) => ({ subject, count }));
};

const extractUniqueSenders = (contacts) =>
  new Set(
    contacts
      .map((contact) => (contact.email || "").trim().toLowerCase())
      .filter(Boolean),
  ).size;

const normalizeFilenameSegment = (value) =>
  `${value || "report"}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "report";

const buildSectionCsvFilename = (section) => {
  const now = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${normalizeFilenameSegment(section)}-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}.csv`;
};

const serializeCsvRow = (cells) =>
  cells
    .map((value) => {
      if (value === null || value === undefined) {
        return "";
      }

      const normalizedValue =
        typeof value === "string"
          ? value
          : typeof value === "number" || typeof value === "boolean"
            ? String(value)
            : JSON.stringify(value);

      if (
        normalizedValue.includes('"') ||
        normalizedValue.includes(",") ||
        normalizedValue.includes("\n")
      ) {
        return `"${normalizedValue.replace(/"/g, '""')}"`;
      }

      return normalizedValue;
    })
    .join(",");

const appendCsvSection = (rows, headers, records = []) => {
  if (rows.length) {
    rows.push("");
  }

  rows.push(serializeCsvRow(headers));
  records.forEach((record) => rows.push(serializeCsvRow(record)));
};

const downloadCsvFile = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [engagementAnalytics, setEngagementAnalytics] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [articleCount, setArticleCount] = useState(0);
  const [trafficRange, setTrafficRange] = useState(DEFAULT_TRAFFIC_RANGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [exportingCsv, setExportingCsv] = useState(false);
  const [allVisitorRecords, setAllVisitorRecords] = useState(null);
  const [loadingAllVisitors, setLoadingAllVisitors] = useState(false);
  const [visitorRecordsError, setVisitorRecordsError] = useState("");
  const [visitorSearch, setVisitorSearch] = useState("");
  const [visitorLocationFilter, setVisitorLocationFilter] = useState("all");
  const [visitorRowsShown, setVisitorRowsShown] = useState(10);

  const [androidApps, setAndroidApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState("");
  const [appForm, setAppForm] = useState(EMPTY_APP_FORM);
  const [appFormSubmitting, setAppFormSubmitting] = useState(false);
  const [appFormSuccess, setAppFormSuccess] = useState("");
  const [appFormError, setAppFormError] = useState("");
  const [deletingAppId, setDeletingAppId] = useState(null);
  const [editingApp, setEditingApp] = useState(null);
  const [qrApp, setQrApp] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrGenerating, setQrGenerating] = useState(false);
  const appFormRef = useRef(null);

  useEffect(() => {
    const token = getAuthToken();

    if (!token) {
      navigate("/login");
      return;
    }

    let isActive = true;

    const loadDashboard = async () => {
      try {
        const [
          analyticsResult,
          engagementResult,
          messagesResult,
          articlesResult,
        ] =
          await Promise.allSettled([
            fetchVisitorAnalytics(token),
            fetchArticleEngagementAnalytics(token),
            fetchMessages(token),
            axios.get(API_ENDPOINTS.getArticles),
          ]);

        const unauthorizedResult = [
          analyticsResult,
          engagementResult,
          messagesResult,
        ].find(
          (result) =>
            result.status === "rejected" &&
            axios.isAxiosError(result.reason) &&
            result.reason.response?.status === 401,
        );

        if (unauthorizedResult) {
          clearAuthToken();
          navigate("/login");
          return;
        }

        if (!isActive) {
          return;
        }

        const issues = [];

        if (analyticsResult.status === "fulfilled") {
          setAnalytics(analyticsResult.value);
        } else {
          issues.push("visitor analytics");
        }

        if (engagementResult.status === "fulfilled") {
          setEngagementAnalytics(engagementResult.value);
        } else {
          issues.push("article engagement");
        }

        if (messagesResult.status === "fulfilled") {
          setContacts(messagesResult.value.contacts || []);
        } else {
          issues.push("messages");
        }

        if (
          articlesResult.status === "fulfilled" &&
          articlesResult.value.data?.articles
        ) {
          setArticleCount(articlesResult.value.data.articles.length);
        } else if (articlesResult.status === "rejected") {
          issues.push("article count");
        }

        setError(
          issues.length
            ? `Some dashboard data could not load: ${issues.join(", ")}.`
            : "",
        );
      } catch (loadError) {
        if (isActive) {
          setError("Unable to load the admin dashboard right now.");
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isActive = false;
    };
  }, [navigate]);

  const loadAndroidApps = async () => {
    const token = getAuthToken();
    if (!token) return;
    setAppsLoading(true);
    setAppsError("");
    try {
      const response = await axios.get(ANDROID_APPS_ENDPOINT, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAndroidApps(response.data.apps || []);
    } catch {
      setAppsError("Could not load Android apps.");
    } finally {
      setAppsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "apps") {
      loadAndroidApps();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleAppFormChange = (e) => {
    const { name, value } = e.target;
    setAppForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAppFormSubmit = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    if (!token) { clearAuthToken(); navigate("/login"); return; }
    if (!appForm.name.trim()) { setAppFormError("App name is required."); return; }
    setAppFormSubmitting(true);
    setAppFormError("");
    setAppFormSuccess("");
    const appPayload = {
      ...appForm,
      iconUrl: getDirectImageUrl(appForm.iconUrl),
    };
    try {
      if (editingApp) {
        await axios.patch(`${ANDROID_APPS_ENDPOINT}/${editingApp._id}`, appPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAppFormSuccess("App updated successfully.");
        setEditingApp(null);
      } else {
        await axios.post(ANDROID_APPS_ENDPOINT, appPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAppFormSuccess("App added successfully.");
      }
      setAppForm(EMPTY_APP_FORM);
      loadAndroidApps();
    } catch (err) {
      setAppFormError(err.response?.data?.message || "Failed to save app.");
    } finally {
      setAppFormSubmitting(false);
    }
  };

  const handleEditApp = (app) => {
    setEditingApp(app);
    setAppFormSuccess("");
    setAppFormError("");
    setAppForm({
      name: app.name || "",
      description: app.description || "",
      packageName: app.packageName || "",
      category: app.category || "General",
      status: app.status || "active",
      iconUrl: getDirectImageUrl(app.iconUrl),
      rating: app.rating === null || app.rating === undefined ? "" : app.rating,
      playStoreLink: app.playStoreLink || "",
      appStoreLink: app.appStoreLink || "",
      githubLink: app.githubLink || "",
      releasedAt: app.releasedAt ? app.releasedAt.slice(0, 10) : "",
      notes: app.notes || "",
    });
    appFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCancelEdit = () => {
    setEditingApp(null);
    setAppForm(EMPTY_APP_FORM);
    setAppFormError("");
    setAppFormSuccess("");
  };

  const handleDeleteApp = async (id) => {
    const token = getAuthToken();
    if (!token) { clearAuthToken(); navigate("/login"); return; }
    setDeletingAppId(id);
    try {
      await axios.delete(`${ANDROID_APPS_ENDPOINT}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAndroidApps((prev) => prev.filter((app) => app._id !== id));
    } catch {
      setAppsError("Failed to delete app.");
    } finally {
      setDeletingAppId(null);
    }
  };

  const openQrModal = async (app) => {
    setQrApp(app);
    setQrDataUrl("");
    setQrGenerating(true);
    try {
      const QRCode = await import("qrcode");
      const url = await QRCode.toDataURL(app.playStoreLink, {
        width: 300,
        margin: 1,
        color: { dark: "#000000", light: "#ffffff" },
      });
      setQrDataUrl(url);
    } catch {
      setQrDataUrl("");
    } finally {
      setQrGenerating(false);
    }
  };

  const downloadBrandedQr = async (app) => {
    const QRCode = await import("qrcode");
    const CARD_W = 380;
    const BADGE_H = 90;
    const QR_SIZE = 280;
    const PADDING = 20;
    const FOOTER_H = 50;
    const CARD_H = BADGE_H + QR_SIZE + PADDING * 2 + FOOTER_H;

    const canvas = document.createElement("canvas");
    canvas.width = CARD_W;
    canvas.height = CARD_H;
    const ctx = canvas.getContext("2d");

    // White background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, CARD_W, CARD_H);

    // Google Play green header
    ctx.fillStyle = "#01875f";
    ctx.fillRect(0, 0, CARD_W, BADGE_H);

    // Play Store triangle icon
    const triX = 24, triY = 22, triH = 46;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.moveTo(triX, triY);
    ctx.lineTo(triX + triH * 0.65, triY + triH / 2);
    ctx.lineTo(triX, triY + triH);
    ctx.closePath();
    ctx.fill();

    // "Get it on" text
    ctx.fillStyle = "rgba(255,255,255,0.82)";
    ctx.font = "500 13px -apple-system, BlinkMacSystemFont, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("GET IT ON", 80, 34);

    // "Google Play" text
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 22px -apple-system, BlinkMacSystemFont, sans-serif";
    ctx.fillText("Google Play", 80, 62);

    // QR code image
    const qrDataUrl = await QRCode.toDataURL(app.playStoreLink, {
      width: QR_SIZE,
      margin: 1,
      color: { dark: "#000000", light: "#ffffff" },
    });
    const qrImg = new Image();
    await new Promise((res) => { qrImg.onload = res; qrImg.src = qrDataUrl; });
    const qrX = (CARD_W - QR_SIZE) / 2;
    ctx.drawImage(qrImg, qrX, BADGE_H + PADDING, QR_SIZE, QR_SIZE);

    // App name footer
    ctx.fillStyle = "#0b2238";
    ctx.font = "bold 15px -apple-system, BlinkMacSystemFont, sans-serif";
    ctx.textAlign = "center";
    const footerY = BADGE_H + PADDING + QR_SIZE + 30;
    ctx.fillText(app.name, CARD_W / 2, footerY);

    // Download
    const link = document.createElement("a");
    link.download = `${app.name.replace(/\s+/g, "-")}-playstore-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const summary = useMemo(() => analytics?.summary || {}, [analytics]);
  const engagementSummary = useMemo(
    () => engagementAnalytics?.summary || {},
    [engagementAnalytics],
  );
  const topCountries = useMemo(
    () => analytics?.topCountries || [],
    [analytics],
  );
  const topCities = useMemo(() => analytics?.topCities || [], [analytics]);
  const topReferrers = useMemo(
    () => analytics?.topReferrers || [],
    [analytics],
  );
  const topSources = useMemo(() => analytics?.topSources || [], [analytics]);
  const topChannels = useMemo(() => analytics?.topChannels || [], [analytics]);
  const topCampaigns = useMemo(
    () => analytics?.topCampaigns || [],
    [analytics],
  );
  const topLandingPages = useMemo(
    () => analytics?.topLandingPages || [],
    [analytics],
  );
  const popularPages = useMemo(
    () => analytics?.popularPages || [],
    [analytics],
  );
  const loadedVisitorRecords = useMemo(
    () => allVisitorRecords || analytics?.recentVisitors || [],
    [allVisitorRecords, analytics],
  );
  const filteredVisitorRecords = useMemo(() => {
    const normalizedSearch = visitorSearch.trim().toLowerCase();

    return loadedVisitorRecords.filter((visitor) => {
      const cityResolved = hasResolvedCity(visitor);
      const matchesLocationFilter =
        visitorLocationFilter === "all" ||
        (visitorLocationFilter === "resolved" && cityResolved) ||
        (visitorLocationFilter === "unresolved" && !cityResolved);

      if (!matchesLocationFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [
        visitor.visitorId,
        visitor.ipAddress,
        visitor.city,
        visitor.region,
        visitor.country,
        getCountryLabel(visitor.country),
        visitor.lastPage,
        visitor.referrer,
        getVisitorAcquisition(visitor).source,
        getVisitorAcquisition(visitor).channel,
        getVisitorAcquisition(visitor).landingPage,
      ].some((value) => `${value || ""}`.toLowerCase().includes(normalizedSearch));
    });
  }, [loadedVisitorRecords, visitorLocationFilter, visitorSearch]);
  const recentVisitors = useMemo(
    () => filteredVisitorRecords.slice(0, visitorRowsShown),
    [filteredVisitorRecords, visitorRowsShown],
  );
  const visitorRecordsMeta = useMemo(
    () => analytics?.visitorRecords || {},
    [analytics],
  );
  const topEngagedArticles = useMemo(
    () => engagementAnalytics?.topArticles || [],
    [engagementAnalytics],
  );
  const subjectBreakdown = useMemo(
    () => groupMessagesBySubject(contacts),
    [contacts],
  );
  const uniqueSenders = useMemo(
    () => extractUniqueSenders(contacts),
    [contacts],
  );
  const selectedTrafficRange = useMemo(
    () =>
      TRAFFIC_RANGE_OPTIONS.find((option) => option.value === trafficRange) ||
      TRAFFIC_RANGE_OPTIONS[TRAFFIC_RANGE_OPTIONS.length - 1],
    [trafficRange],
  );
  const trafficTrend = useMemo(
    () => buildTrafficTrend(analytics?.dailyTraffic || [], trafficRange),
    [analytics, trafficRange],
  );
  const messageTrend = useMemo(() => buildMessageTrend(contacts), [contacts]);

  const totalMessagesLast7Days = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);

    return contacts.filter(
      (contact) => new Date(contact.createdAt || contact.timestamp) >= cutoff,
    ).length;
  }, [contacts]);

  const trafficChartData = useMemo(
    () => ({
      labels: trafficTrend.map((point) => point.label),
      datasets: [
        {
          label: "Unique visitors",
          data: trafficTrend.map((point) => point.uniqueVisitors),
          borderColor: "#1d4ed8",
          backgroundColor: "rgba(29, 78, 216, 0.14)",
          tension: 0.36,
          fill: true,
          borderWidth: 3,
          pointRadius: trafficTrend.length > 60 ? 0 : 3,
        },
        {
          label: "Page views",
          data: trafficTrend.map((point) => point.pageViews),
          borderColor: "#0f766e",
          backgroundColor: "rgba(15, 118, 110, 0.12)",
          tension: 0.36,
          fill: false,
          borderWidth: 2,
          pointRadius: trafficTrend.length > 60 ? 0 : 3,
        },
      ],
    }),
    [trafficTrend],
  );

  const trafficChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            boxWidth: 12,
            color: "#12304a",
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: "rgba(18, 48, 74, 0.08)",
          },
          ticks: {
            color: "#4a6279",
            autoSkip: true,
            maxRotation: 0,
            maxTicksLimit:
              trafficTrend.length > 180
                ? 8
                : trafficTrend.length > 90
                  ? 10
                  : trafficTrend.length > 45
                    ? 12
                    : 14,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(18, 48, 74, 0.08)",
          },
          ticks: {
            color: "#4a6279",
            precision: 0,
          },
        },
      },
    }),
    [trafficTrend.length],
  );

  const pageChartData = useMemo(
    () => ({
      labels: popularPages.map((page) => formatPagePath(page.pageUrl)),
      datasets: [
        {
          label: "Page views",
          data: popularPages.map((page) => page.pageViews),
          backgroundColor: [
            "#1d4ed8",
            "#0f766e",
            "#f59e0b",
            "#ef4444",
            "#7c3aed",
            "#0891b2",
            "#16a34a",
            "#9333ea",
          ],
          borderRadius: 10,
        },
      ],
    }),
    [popularPages],
  );

  const pageChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: "rgba(18, 48, 74, 0.08)",
          },
          ticks: {
            color: "#4a6279",
            precision: 0,
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#12304a",
          },
        },
      },
    }),
    [],
  );

  const engagementChartData = useMemo(
    () => ({
      labels: topEngagedArticles.map((article) =>
        truncateText(getArticleDisplayLabel(article), 28),
      ),
      datasets: [
        {
          label: "Likes",
          data: topEngagedArticles.map((article) => article.likes || 0),
          backgroundColor: "#ef4444",
          borderRadius: 10,
        },
        {
          label: "Comments",
          data: topEngagedArticles.map((article) => article.commentsCount || 0),
          backgroundColor: "#f59e0b",
          borderRadius: 10,
        },
        {
          label: "Shares",
          data: topEngagedArticles.map((article) => article.shares || 0),
          backgroundColor: "#0891b2",
          borderRadius: 10,
        },
      ],
    }),
    [topEngagedArticles],
  );

  const engagementChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            boxWidth: 12,
            color: "#12304a",
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#12304a",
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(18, 48, 74, 0.08)",
          },
          ticks: {
            color: "#4a6279",
            precision: 0,
          },
        },
      },
    }),
    [],
  );

  const downloadMetricCsv = ({ label, value, helper }) => {
    const rows = [];
    appendCsvSection(rows, ["Metric", "Value", "Context", "Generated at"], [
      [
        label,
        typeof value === "number" ? value.toLocaleString() : value,
        helper || "",
        new Date().toISOString(),
      ],
    ]);
    downloadCsvFile(buildSectionCsvFilename(label), rows.join("\n"));
  };

  const downloadTrafficCsv = () => {
    const rows = [];
    appendCsvSection(rows, ["Metric", "Value"], [
      ["Range", selectedTrafficRange.heading],
      ["Active visitors in last 30 days", summary.activeVisitorsLast30Days || 0],
      ["Total unique visitors", summary.totalUniqueVisitors || 0],
      ["Total page views", summary.totalPageViews || 0],
    ]);
    appendCsvSection(
      rows,
      ["Date", "Label", "Unique visitors", "Page views"],
      trafficTrend.map((entry) => [
        entry.iso || "",
        entry.label || "",
        entry.uniqueVisitors || 0,
        entry.pageViews || 0,
      ]),
    );
    downloadCsvFile(buildSectionCsvFilename("traffic-trend"), rows.join("\n"));
  };

  const downloadLeadFlowCsv = () => {
    const rows = [];
    appendCsvSection(rows, ["Metric", "Value"], [
      ["Messages in last 7 days", totalMessagesLast7Days],
      ["Unique senders", uniqueSenders],
      ["Top subject", subjectBreakdown[0]?.subject || ""],
      ["Total messages", contacts.length],
    ]);
    appendCsvSection(
      rows,
      ["Date", "Label", "Messages"],
      messageTrend.map((point) => [point.iso, point.label, point.count]),
    );
    appendCsvSection(
      rows,
      ["Subject", "Messages"],
      subjectBreakdown.map((subject) => [subject.subject, subject.count]),
    );
    downloadCsvFile(buildSectionCsvFilename("lead-flow"), rows.join("\n"));
  };

  const downloadAcquisitionCsv = () => {
    const rows = [];
    appendCsvSection(
      rows,
      ["Source", "Visitors", "Visits"],
      topSources.map((item) => [item.source, item.visitors || 0, item.visits || 0]),
    );
    appendCsvSection(
      rows,
      ["Channel", "Visitors", "Visits"],
      topChannels.map((item) => [
        item.channel,
        item.visitors || 0,
        item.visits || 0,
      ]),
    );
    appendCsvSection(
      rows,
      ["Campaign", "Visitors", "Visits"],
      topCampaigns.map((item) => [
        item.campaign,
        item.visitors || 0,
        item.visits || 0,
      ]),
    );
    appendCsvSection(
      rows,
      ["Landing page", "Visitors", "Visits"],
      topLandingPages.map((item) => [
        item.landingPage,
        item.visitors || 0,
        item.visits || 0,
      ]),
    );
    appendCsvSection(
      rows,
      ["Country", "Visitors"],
      topCountries.map((country) => [
        getCountryLabel(country.country),
        country.visitors || 0,
      ]),
    );
    appendCsvSection(
      rows,
      ["City", "Region", "Country", "Visitors"],
      topCities.map((city) => [
        city.city || "",
        city.region || "",
        getCountryLabel(city.country),
        city.visitors || 0,
      ]),
    );
    appendCsvSection(
      rows,
      ["Referrer", "Visitors"],
      topReferrers.map((referrer) => [
        referrer.referrer || "",
        referrer.visitors || 0,
      ]),
    );
    downloadCsvFile(
      buildSectionCsvFilename("audience-acquisition"),
      rows.join("\n"),
    );
  };

  const downloadPagesCsv = () => {
    const rows = [];
    appendCsvSection(
      rows,
      ["Page path", "Page URL", "Page views", "Unique visitors"],
      popularPages.map((page) => [
        formatPagePath(page.pageUrl),
        page.pageUrl || "",
        page.pageViews || 0,
        page.uniqueVisitors || 0,
      ]),
    );
    downloadCsvFile(buildSectionCsvFilename("page-performance"), rows.join("\n"));
  };

  const downloadArticleEngagementCsv = () => {
    const rows = [];
    appendCsvSection(rows, ["Metric", "Value"], [
      ["Published articles", articleCount],
      ["Tracked articles", engagementSummary.trackedArticles || 0],
      ["Total likes", engagementSummary.totalLikes || 0],
      ["Total comments", engagementSummary.totalComments || 0],
      ["Total shares", engagementSummary.totalShares || 0],
      ["Total interactions", engagementSummary.totalEngagements || 0],
    ]);
    appendCsvSection(
      rows,
      ["Article", "Slug", "Likes", "Comments", "Shares", "Total interactions"],
      topEngagedArticles.map((article) => [
        getArticleDisplayLabel(article),
        article.slug || "",
        article.likes || 0,
        article.commentsCount || 0,
        article.shares || 0,
        article.totalEngagements || 0,
      ]),
    );
    downloadCsvFile(
      buildSectionCsvFilename("article-engagement"),
      rows.join("\n"),
    );
  };

  const downloadVisitorsCsv = () => {
    const token = getAuthToken();
    if (!token) { clearAuthToken(); navigate("/login"); return; }
    setDownloadError("");
    setExportingCsv(true);
    fetchVisitorAnalytics(token, { includeAllVisitors: true })
      .then((fullAnalytics) => {
        const allVisitors = fullAnalytics?.allVisitors || fullAnalytics?.recentVisitors || [];
        const rows = [];
        appendCsvSection(
          rows,
          [
            "Visitor ID",
            "IP",
            "City",
            "Region",
            "Country",
            "Timezone",
            "Source",
            "Channel",
            "Campaign",
            "Referrer origin",
            "Landing page",
            "Redirect count",
            "Device",
            "Browser",
            "First page",
            "Last page",
            "Visits",
            "Page views",
            "Time spent",
            "First seen",
            "Last seen",
          ],
          allVisitors.map((v) => {
            const acquisition = getVisitorAcquisition(v);
            return [
              v.visitorId || "",
              v.ipAddress || "",
              v.city || "",
              v.region || "",
              getCountryLabel(v.country),
              v.timezone || "",
              acquisition.source || "",
              acquisition.channel || "",
              acquisition.campaign || "",
              acquisition.referrerHost || "",
              acquisition.landingPage || "",
              acquisition.redirectCount || 0,
              v.device?.type || "",
              v.device?.browser || "",
              formatPagePath(v.firstPage),
              formatPagePath(v.lastPage),
              v.visitCount || 0,
              v.pageViewCount || 0,
              formatDuration(v.totalEngagementMs),
              v.firstSeenAt || "",
              v.lastSeenAt || "",
            ];
          })
        );
        downloadCsvFile(buildSectionCsvFilename("visitor-records"), rows.join("\n"));
      })
      .catch((err) => {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          clearAuthToken(); navigate("/login"); return;
        }
        setDownloadError("Unable to download visitor data right now.");
      })
      .finally(() => setExportingCsv(false));
  };

  const loadAllVisitorRecords = () => {
    const token = getAuthToken();

    if (!token) {
      clearAuthToken();
      navigate("/login");
      return;
    }

    setVisitorRecordsError("");
    setLoadingAllVisitors(true);
    fetchVisitorAnalytics(token, { includeAllVisitors: true })
      .then((fullAnalytics) => {
        setAllVisitorRecords(
          fullAnalytics?.allVisitors || fullAnalytics?.recentVisitors || [],
        );
      })
      .catch((loadError) => {
        if (axios.isAxiosError(loadError) && loadError.response?.status === 401) {
          clearAuthToken();
          navigate("/login");
          return;
        }

        setVisitorRecordsError("Unable to load the full visitor archive right now.");
      })
      .finally(() => setLoadingAllVisitors(false));
  };

  return (
    <div className="dashboard-page">
      {/* ── Compact Hero ─────────────────────────────────── */}
      <div className="container pt-4 pb-3">
        <div className="dash-hero-compact">
          <div className="dash-hero-left">
            <p className="dashboard-eyebrow mb-1">Private analytics</p>
            <h1 className="h3 fw-bold mb-0">Website intelligence</h1>
            {!loading && summary.lastUpdatedAt && (
              <p className="dash-hero-ts mb-0 mt-1">
                Last updated {formatDateTime(summary.lastUpdatedAt)}
              </p>
            )}
          </div>
          <div className="dash-hero-actions">
            <Link to="/postanarticle" className="btn btn-light btn-sm">
              <FilePlus2 size={14} className="me-2" />
              Post article
            </Link>
            <Link to="/" className="btn btn-outline-light btn-sm">
              <ArrowUpRight size={14} className="me-2" />
              View site
            </Link>
          </div>
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────── */}
      <div className="container pb-3">
        <div className="dashboard-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={`dashboard-tab-btn${activeTab === "analytics" ? " active" : ""}`}
            onClick={() => setActiveTab("analytics")}
          >
            <Activity size={15} className="me-2" />
            Website analytics
          </button>
          <button
            type="button"
            role="tab"
            className={`dashboard-tab-btn${activeTab === "apps" ? " active" : ""}`}
            onClick={() => setActiveTab("apps")}
          >
            <Smartphone size={15} className="me-2" />
            App registry
          </button>
        </div>
      </div>

      {activeTab === "apps" && (
        <section className="container pb-5">
          <div className="apps-control-sheet">
            <div className="card-head">
              <div>
                <p className="dashboard-eyebrow mb-1">App registry</p>
                <h2 className="h4 fw-bold mb-1">Android Apps Control Sheet</h2>
                <p className="text-muted mb-0">
                  Track all published Google Play apps, their GitHub repos, and production status.
                </p>
              </div>
              <div className="stat-pill">
                <Smartphone size={16} className="me-2" />
                {androidApps.length} app{androidApps.length !== 1 ? "s" : ""}
              </div>
            </div>

            <div className="apps-form-section" ref={appFormRef}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <h3 className="h5 fw-bold mb-0">
                  {editingApp ? `Editing: ${editingApp.name}` : "Add New App"}
                </h3>
                {editingApp && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleCancelEdit}
                  >
                    Cancel edit
                  </button>
                )}
              </div>
              {appFormSuccess && (
                <div className="alert alert-success py-2">{appFormSuccess}</div>
              )}
              {appFormError && (
                <div className="alert alert-danger py-2">{appFormError}</div>
              )}
              <form onSubmit={handleAppFormSubmit} className="apps-form">
                <div className="apps-form-grid">
                  <div className="form-group">
                    <label className="form-label apps-label">App Name *</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control apps-input"
                      placeholder="e.g. Trans Voyage Driver"
                      value={appForm.name}
                      onChange={handleAppFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">Package Name</label>
                    <input
                      type="text"
                      name="packageName"
                      className="form-control apps-input"
                      placeholder="com.example.app"
                      value={appForm.packageName}
                      onChange={handleAppFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">Category</label>
                    <select
                      name="category"
                      className="form-select apps-input"
                      value={appForm.category}
                      onChange={handleAppFormChange}
                    >
                      {APP_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">Status</label>
                    <select
                      name="status"
                      className="form-select apps-input"
                      value={appForm.status}
                      onChange={handleAppFormChange}
                    >
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="deprecated">Deprecated</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">Icon URL</label>
                    <div className="apps-icon-input-row">
                      {appForm.iconUrl && (
                        <img
                          src={getDirectImageUrl(appForm.iconUrl)}
                          alt="Icon preview"
                          className="apps-icon-preview"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      )}
                      <input
                        type="url"
                        name="iconUrl"
                        className="form-control apps-input"
                        placeholder="https://.../icon.png"
                        value={appForm.iconUrl}
                        onChange={handleAppFormChange}
                        onBlur={() =>
                          setAppForm((prev) => ({
                            ...prev,
                            iconUrl: getDirectImageUrl(prev.iconUrl),
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">Rating (0-5)</label>
                    <input
                      type="number"
                      name="rating"
                      className="form-control apps-input"
                      placeholder="e.g. 4.5"
                      min="0"
                      max="5"
                      step="0.1"
                      value={appForm.rating}
                      onChange={handleAppFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">Play Store Link</label>
                    <input
                      type="url"
                      name="playStoreLink"
                      className="form-control apps-input"
                      placeholder="https://play.google.com/store/apps/details?id=..."
                      value={appForm.playStoreLink}
                      onChange={handleAppFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">App Store Link</label>
                    <input
                      type="url"
                      name="appStoreLink"
                      className="form-control apps-input"
                      placeholder="https://apps.apple.com/app/..."
                      value={appForm.appStoreLink}
                      onChange={handleAppFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">GitHub Link</label>
                    <input
                      type="url"
                      name="githubLink"
                      className="form-control apps-input"
                      placeholder="https://github.com/OldAlexhub/..."
                      value={appForm.githubLink}
                      onChange={handleAppFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label apps-label">Release Date</label>
                    <input
                      type="date"
                      name="releasedAt"
                      className="form-control apps-input"
                      value={appForm.releasedAt}
                      onChange={handleAppFormChange}
                    />
                  </div>
                  <div className="form-group apps-form-col-full">
                    <label className="form-label apps-label">Description</label>
                    <textarea
                      name="description"
                      className="form-control apps-input"
                      rows={2}
                      placeholder="Short description of the app..."
                      value={appForm.description}
                      onChange={handleAppFormChange}
                    />
                  </div>
                  <div className="form-group apps-form-col-full">
                    <label className="form-label apps-label">Internal Notes</label>
                    <textarea
                      name="notes"
                      className="form-control apps-input"
                      rows={2}
                      placeholder="Private notes (not shown publicly)..."
                      value={appForm.notes}
                      onChange={handleAppFormChange}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary mt-3"
                  disabled={appFormSubmitting}
                >
                  {appFormSubmitting
                    ? (editingApp ? "Saving..." : "Adding...")
                    : (editingApp ? "Save Changes" : "Add App")}
                </button>
              </form>
            </div>

            <div className="apps-table-section mt-4">
              <h3 className="h5 fw-bold mb-3">Published Apps</h3>
              {appsError && <div className="alert alert-warning py-2">{appsError}</div>}
              {appsLoading ? (
                <div className="empty-panel">Loading apps...</div>
              ) : androidApps.length === 0 ? (
                <div className="empty-panel">No apps added yet. Use the form above to register your first app.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0 dashboard-table apps-table">
                    <thead>
                      <tr>
                        <th>Icon</th>
                        <th>App Name</th>
                        <th>Package</th>
                        <th>Category</th>
                        <th>Rating</th>
                        <th>Status</th>
                        <th>Released</th>
                        <th>Play Store</th>
                        <th>App Store</th>
                        <th>GitHub</th>
                        <th>Notes</th>
                        <th>QR</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {androidApps.map((app) => (
                        <tr key={app._id}>
                          <td>
                            {app.iconUrl ? (
                              <img
                                src={getDirectImageUrl(app.iconUrl)}
                                alt=""
                                className="apps-table-icon"
                              />
                            ) : (
                              <div className="apps-table-icon apps-table-icon--placeholder" />
                            )}
                          </td>
                          <td>
                            <div className="fw-semibold">{app.name}</div>
                            {app.description && (
                              <div className="text-muted small">{app.description}</div>
                            )}
                          </td>
                          <td className="text-muted small text-nowrap">{app.packageName || "-"}</td>
                          <td>
                            <span className="apps-category-chip">{app.category}</span>
                          </td>
                          <td className="text-nowrap">
                            {app.rating === null || app.rating === undefined ? (
                              <span className="text-muted small">-</span>
                            ) : (
                              <span className="apps-rating-chip">★ {Number(app.rating).toFixed(1)}</span>
                            )}
                          </td>
                          <td>
                            <span className={`apps-status-badge apps-status-${app.status}`}>
                              {app.status}
                            </span>
                          </td>
                          <td className="text-nowrap text-muted small">
                            {app.releasedAt
                              ? new Date(app.releasedAt).toLocaleDateString()
                              : "-"}
                          </td>
                          <td>
                            {app.playStoreLink ? (
                              <a
                                href={app.playStoreLink}
                                target="_blank"
                                rel="noreferrer"
                                className="apps-link"
                              >
                                Play Store
                              </a>
                            ) : "-"}
                          </td>
                          <td>
                            {app.appStoreLink ? (
                              <a
                                href={app.appStoreLink}
                                target="_blank"
                                rel="noreferrer"
                                className="apps-link"
                              >
                                App Store
                              </a>
                            ) : "-"}
                          </td>
                          <td>
                            {app.githubLink ? (
                              <a
                                href={app.githubLink}
                                target="_blank"
                                rel="noreferrer"
                                className="apps-link"
                              >
                                GitHub
                              </a>
                            ) : "-"}
                          </td>
                          <td className="text-muted small">
                            {app.notes ? truncateText(app.notes, 60) : "-"}
                          </td>
                          <td>
                            {app.playStoreLink ? (
                              <button
                                type="button"
                                className="btn btn-sm apps-qr-btn"
                                onClick={() => openQrModal(app)}
                                title="Generate Play Store QR code"
                              >
                                QR
                              </button>
                            ) : "-"}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleEditApp(app)}
                                title="Edit app"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteApp(app._id)}
                                disabled={deletingAppId === app._id}
                                title="Delete app"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {activeTab === "analytics" && (
        <WebsiteAnalyticsDashboard
          analytics={analytics}
          engagementAnalytics={engagementAnalytics}
          contacts={contacts}
          articleCount={articleCount}
          loading={loading}
          error={error}
          downloadError={downloadError}
          trafficRange={trafficRange}
          trafficRangeOptions={TRAFFIC_RANGE_OPTIONS}
          selectedTrafficRange={selectedTrafficRange}
          setTrafficRange={setTrafficRange}
          trafficChartData={trafficChartData}
          trafficChartOptions={trafficChartOptions}
          pageChartData={pageChartData}
          pageChartOptions={pageChartOptions}
          engagementChartData={engagementChartData}
          engagementChartOptions={engagementChartOptions}
          visitorState={{
            search: visitorSearch,
            setSearch: setVisitorSearch,
            locationFilter: visitorLocationFilter,
            setLocationFilter: setVisitorLocationFilter,
            rowsShown: visitorRowsShown,
            setRowsShown: setVisitorRowsShown,
            loadedRecords: loadedVisitorRecords,
            filteredRecords: filteredVisitorRecords,
            recentRecords: recentVisitors,
            meta: visitorRecordsMeta,
            allRecords: allVisitorRecords,
            loadingAll: loadingAllVisitors,
            loadAll: loadAllVisitorRecords,
            error: visitorRecordsError,
            exportingCsv,
          }}
          reports={{
            metric: downloadMetricCsv,
            traffic: downloadTrafficCsv,
            acquisition: downloadAcquisitionCsv,
            pages: downloadPagesCsv,
            articleEngagement: downloadArticleEngagementCsv,
            leadFlow: downloadLeadFlowCsv,
            visitors: downloadVisitorsCsv,
          }}
        />
      )}

      {/* Legacy analytics markup retained temporarily for reference.
      <>
      {!loading ? (
      <section className="container pb-4">
        <div className="dashboard-metric-grid">
          {metricCards.map((metric) => {
            const { label, value, helper, icon: Icon, tone } = metric;

            return (
            <div key={label} className={`dashboard-metric-card tone-${tone}`}>
              <div className="metric-card-top">
                <div className="metric-icon-wrap">
                  <Icon size={18} />
                </div>
                <button
                  type="button"
                  className="metric-report-button"
                  onClick={() => downloadMetricCsv(metric)}
                  title={`Download ${label} report`}
                  aria-label={`Download ${label} report`}
                >
                  <Download size={14} />
                </button>
              </div>
              <div className="metric-value">
                {typeof value === "number" ? value.toLocaleString() : value}
              </div>
              <div className="metric-label">{label}</div>
              <div className="metric-helper">{helper}</div>
            </div>
            );
          })}
        </div>
      </section>
      ) : null}

      {error ? (
        <section className="container pb-4">
          <div className="alert alert-warning mb-0">{error}</div>
        </section>
      ) : null}

      {downloadError ? (
        <section className="container pb-4">
          <div className="alert alert-danger mb-0">{downloadError}</div>
        </section>
      ) : null}

      {loading ? (
        <section className="container pb-5">
          <div className="dashboard-loading-shell">
            <div className="dashboard-loading-card" />
            <div className="dashboard-loading-card" />
            <div className="dashboard-loading-card" />
          </div>
        </section>
      ) : (
        <>
          <section className="container pb-4">
            <div className="dashboard-grid dashboard-grid-primary">
              <div className="dashboard-card chart-card">
                <div className="card-head">
                  <div>
                    <p className="dashboard-eyebrow mb-1">Traffic trend</p>
                    <h2 className="h4 fw-bold mb-1">
                      {selectedTrafficRange.heading}
                    </h2>
                    <p className="text-muted mb-0">
                      Unique visitors and total page views captured from live
                      traffic.
                    </p>
                  </div>
                  <div className="card-head-actions">
                    <ReportButton
                      onClick={downloadTrafficCsv}
                      disabled={!analytics}
                    />
                    <div className="stat-pill">
                      <Radar size={16} className="me-2" />
                      {summary.activeVisitorsLast30Days || 0} active in 30 days
                    </div>
                    <div
                      className="traffic-range-controls"
                      role="group"
                      aria-label="Traffic trend range"
                    >
                      {TRAFFIC_RANGE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`traffic-range-button${
                            trafficRange === option.value ? " active" : ""
                          }`}
                          onClick={() => setTrafficRange(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="chart-shell chart-shell-lg">
                  <Line data={trafficChartData} options={trafficChartOptions} />
                </div>
              </div>

              <div className="dashboard-stack">
                <div className="dashboard-card insight-card">
                  <div className="card-head compact">
                    <div>
                      <p className="dashboard-eyebrow mb-1">Lead flow</p>
                      <h3 className="h5 fw-bold mb-0">Message pulse</h3>
                    </div>
                    <ReportButton
                      onClick={downloadLeadFlowCsv}
                      disabled={!contacts.length}
                    />
                  </div>
                  <div className="mini-stat-grid">
                    <div>
                      <span className="mini-label">Last 7 days</span>
                      <strong>{totalMessagesLast7Days}</strong>
                    </div>
                    <div>
                      <span className="mini-label">Unique senders</span>
                      <strong>{uniqueSenders}</strong>
                    </div>
                    <div>
                      <span className="mini-label">Top subject</span>
                      <strong>
                        {subjectBreakdown[0]?.subject || "No messages yet"}
                      </strong>
                    </div>
                  </div>
                  <div className="sparkline-row">
                    {messageTrend.map((point) => (
                      <div
                        key={point.iso}
                        className="sparkline-bar-wrap"
                        title={`${point.label}: ${point.count} messages`}
                      >
                        <div
                          className="sparkline-bar"
                          style={{
                            height: `${Math.max(
                              8,
                              Math.round((point.count / messageTrendMax) * 56),
                            )}px`,
                          }}
                        />
                        <span>{point.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dashboard-card list-card audience-card">
                  <div className="card-head compact">
                    <div>
                      <p className="dashboard-eyebrow mb-1">Audience</p>
                      <h3 className="h5 fw-bold mb-0">Reach and sources</h3>
                    </div>
                    <ReportButton
                      onClick={downloadAcquisitionCsv}
                      disabled={!topCountries.length && !topReferrers.length}
                    />
                  </div>
                  <div className="audience-columns">
                    <div>
                      <div className="mini-section-label">
                        <MapPinned size={14} />
                        Countries
                      </div>
                      {topCountries.length ? (
                        <ul className="signal-list">
                          {topCountries.slice(0, 4).map((item) => (
                            <li key={item.country}>
                              <span>{getCountryLabel(item.country)}</span>
                              <strong>{item.visitors}</strong>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted mb-0">No country data yet.</p>
                      )}
                    </div>
                    <div>
                      <div className="mini-section-label">
                        <Route size={14} />
                        Referrers
                      </div>
                      {topReferrers.length ? (
                        <ul className="signal-list signal-list-break">
                          {topReferrers.slice(0, 4).map((item) => (
                            <li key={item.referrer}>
                              <span>{item.referrer}</span>
                              <strong>{item.visitors}</strong>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted mb-0">
                          Direct traffic is currently leading.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="container pb-4">
            <div className="dashboard-grid dashboard-grid-secondary">
              <div className="dashboard-card chart-card">
                <div className="card-head">
                  <div>
                    <p className="dashboard-eyebrow mb-1">Page performance</p>
                    <h2 className="h4 fw-bold mb-1">Most visited pages</h2>
                    <p className="text-muted mb-0">
                      Page views and unique visitors by URL.
                    </p>
                  </div>
                  <ReportButton
                    onClick={downloadPagesCsv}
                    disabled={!popularPages.length}
                  />
                </div>
                <div className="chart-shell chart-shell-md">
                  {popularPages.length ? (
                    <Bar data={pageChartData} options={pageChartOptions} />
                  ) : (
                    <div className="chart-empty-state">
                      Traffic needs to accumulate before page performance trends
                      appear.
                    </div>
                  )}
                </div>
              </div>

              <div className="dashboard-card chart-card">
                <div className="card-head">
                  <div>
                    <p className="dashboard-eyebrow mb-1">Article engagement</p>
                    <h2 className="h4 fw-bold mb-1">Top interactions</h2>
                    <p className="text-muted mb-0">
                      Likes, comments, and shares by article.
                    </p>
                  </div>
                  <ReportButton
                    onClick={downloadArticleEngagementCsv}
                    disabled={!topEngagedArticles.length}
                  />
                </div>
                <div className="article-summary-strip">
                  <span>{articleCount} published</span>
                  <span>{engagementSummary.totalLikes || 0} likes</span>
                  <span>{engagementSummary.totalComments || 0} comments</span>
                  <span>{engagementSummary.totalShares || 0} shares</span>
                </div>
                <div className="chart-shell chart-shell-md">
                  {topEngagedArticles.length ? (
                    <Bar
                      data={engagementChartData}
                      options={engagementChartOptions}
                    />
                  ) : (
                    <div className="chart-empty-state">
                      Reader interaction will appear here once likes, comments,
                      or shares start landing.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="container pb-4">
            <div className="dashboard-grid dashboard-grid-operations">
              <div className="dashboard-card">
                <div className="card-head">
                  <div>
                    <p className="dashboard-eyebrow mb-1">Client signals</p>
                    <h2 className="h4 fw-bold mb-1">
                      Storage and cookie snapshot
                    </h2>
                    <p className="text-muted mb-0">
                      Browser-readable cookies, local/session storage, and Cache
                      Storage usage captured from each visitor device.
                    </p>
                  </div>
                  <div className="card-head-actions">
                    <div className="stat-pill">
                      <Database size={16} className="me-2" />
                      {storageSignalCoverage}% coverage
                    </div>
                    <ReportButton
                      onClick={downloadStorageCsv}
                      disabled={!storageInsights.trackedProfiles}
                    />
                  </div>
                </div>

                <div className="storage-stat-grid">
                  <div className="storage-stat-panel">
                    <span className="mini-label">Profiles with snapshot</span>
                    <strong>
                      {(storageInsights.trackedProfiles || 0).toLocaleString()}
                    </strong>
                  </div>
                  <div className="storage-stat-panel">
                    <span className="mini-label">Cookies enabled</span>
                    <strong>{cookieEnabledRate}%</strong>
                  </div>
                  <div className="storage-stat-panel">
                    <span className="mini-label">Avg local footprint</span>
                    <strong>
                      {formatByteSize(
                        storageInsights.averageLocalStorageBytes || 0,
                      )}
                    </strong>
                  </div>
                  <div className="storage-stat-panel">
                    <span className="mini-label">Avg session footprint</span>
                    <strong>
                      {formatByteSize(
                        storageInsights.averageSessionStorageBytes || 0,
                      )}
                    </strong>
                  </div>
                  <div className="storage-stat-panel">
                    <span className="mini-label">
                      Visitors with cached assets
                    </span>
                    <strong>
                      {(
                        storageInsights.visitorsWithCacheEntries || 0
                      ).toLocaleString()}
                    </strong>
                  </div>
                  <div className="storage-stat-panel">
                    <span className="mini-label">Cached assets tracked</span>
                    <strong>
                      {(
                        storageInsights.totalCacheEntries || 0
                      ).toLocaleString()}
                    </strong>
                  </div>
                </div>

                {topCookieNames.length ? (
                  <div className="cookie-chip-wrap">
                    {topCookieNames.slice(0, 6).map((item) => (
                      <span key={item.name} className="cookie-chip">
                        {item.name} ({item.visitors})
                      </span>
                    ))}
                  </div>
                ) : null}

                <p className="storage-note mb-0">
                  This only includes browser-accessible data. HttpOnly cookies
                  and the browser&apos;s network cache are not exposed to page
                  scripts.
                </p>
              </div>

              <div className="dashboard-card visitor-table-card">
                <div className="card-head">
                  <div>
                    <p className="dashboard-eyebrow mb-1">Recent traffic</p>
                    <h2 className="h4 fw-bold mb-1">Latest visitor records</h2>
                    <p className="text-muted mb-0">
                      {visitorRecordsMeta.total || summary.totalUniqueVisitors || 0} visitor
                      {Number(visitorRecordsMeta.total || summary.totalUniqueVisitors) === 1
                        ? " profile is"
                        : " profiles are"}{" "}
                      retained with no automatic deletion.
                    </p>
                  </div>
                  <div className="visitor-card-actions">
                    <span className="visitor-retention-badge">
                      <Database size={15} aria-hidden="true" />
                      All-time retention
                    </span>
                    <ReportButton
                      onClick={downloadVisitorsCsv}
                      disabled={!analytics}
                      busy={exportingCsv}
                    />
                  </div>
                </div>

                <div className="visitor-location-summary">
                  <div>
                    <span>City coverage</span>
                    <strong>
                      {Math.round((summary.cityCoverageRate || 0) * 100)}%
                    </strong>
                    <small>
                      {(summary.cityResolvedVisitors || 0).toLocaleString()} resolved
                    </small>
                  </div>
                  <div>
                    <span>Needs city</span>
                    <strong>
                      {(summary.cityUnknownVisitors || 0).toLocaleString()}
                    </strong>
                    <small>VPN, relay, or IP database gaps</small>
                  </div>
                  <div>
                    <span>Oldest profile</span>
                    <strong className="visitor-summary-date">
                      {visitorRecordsMeta.oldestRetainedAt
                        ? new Date(visitorRecordsMeta.oldestRetainedAt).toLocaleDateString()
                        : "Not available"}
                    </strong>
                    <small>Still retained</small>
                  </div>
                </div>

                <div className="visitor-table-toolbar">
                  <label className="visitor-search-field">
                    <Search size={16} aria-hidden="true" />
                    <span className="visually-hidden">Search visitor records</span>
                    <input
                      type="search"
                      value={visitorSearch}
                      onChange={(event) => {
                        setVisitorSearch(event.target.value);
                        setVisitorRowsShown(10);
                      }}
                      placeholder="Search city, country, IP, or page"
                    />
                  </label>
                  <select
                    className="visitor-filter-select"
                    value={visitorLocationFilter}
                    onChange={(event) => {
                      setVisitorLocationFilter(event.target.value);
                      setVisitorRowsShown(10);
                    }}
                    aria-label="Filter visitors by city coverage"
                  >
                    <option value="all">All locations</option>
                    <option value="resolved">City resolved</option>
                    <option value="unresolved">City unavailable</option>
                  </select>
                  {!allVisitorRecords &&
                  loadedVisitorRecords.length <
                    (visitorRecordsMeta.total || summary.totalUniqueVisitors || 0) ? (
                    <button
                      type="button"
                      className="visitor-archive-button"
                      onClick={loadAllVisitorRecords}
                      disabled={loadingAllVisitors}
                    >
                      {loadingAllVisitors ? "Loading archive..." : "Browse all retained"}
                    </button>
                  ) : null}
                </div>

                <p className="visitor-table-caption">
                  Showing {recentVisitors.length.toLocaleString()} of{" "}
                  {filteredVisitorRecords.length.toLocaleString()} matching records;{" "}
                  {loadedVisitorRecords.length.toLocaleString()} loaded from{" "}
                  {(visitorRecordsMeta.total || summary.totalUniqueVisitors || 0).toLocaleString()}{" "}
                  retained profiles.
                </p>

                {visitorRecordsError ? (
                  <div className="alert alert-warning py-2">{visitorRecordsError}</div>
                ) : null}

                {recentVisitors.length ? (
                  <div className="table-responsive visitor-table-scroll">
                    <table className="table align-middle mb-0 dashboard-table">
                      <thead>
                        <tr>
                          <th>IP</th>
                          <th>Location</th>
                          <th>Last page</th>
                          <th>Visits</th>
                          <th>Time spent</th>
                          <th>Last seen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentVisitors.map((visitor) => (
                          <tr key={visitor._id}>
                            <td className="text-nowrap">
                              {visitor.ipAddress}
                            </td>
                            <td className="visitor-location-cell">
                              <div className="visitor-location-primary">
                                <MapPinned size={16} aria-hidden="true" />
                                <span>
                                  <strong>
                                    {hasResolvedCity(visitor)
                                      ? visitor.city
                                      : "City unavailable"}
                                  </strong>
                                  <small>{formatLocationContext(visitor)}</small>
                                </span>
                              </div>
                              <span
                                className={`visitor-location-status ${
                                  hasResolvedCity(visitor)
                                    ? "visitor-location-status--resolved"
                                    : "visitor-location-status--unresolved"
                                }`}
                              >
                                {getLocationSourceLabel(
                                  visitor.locationSource,
                                  hasResolvedCity(visitor),
                                )}
                              </span>
                            </td>
                            <td className="text-break">
                              {formatPagePath(visitor.lastPage)}
                            </td>
                            <td>{visitor.visitCount}</td>
                            <td className="text-nowrap">
                              {formatDuration(visitor.totalEngagementMs)}
                            </td>
                            <td className="text-nowrap">
                              {formatDateTime(visitor.lastSeenAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-panel">
                    {loadedVisitorRecords.length
                      ? "No visitor records match these filters."
                      : "Visitor records will populate here as traffic arrives."}
                  </div>
                )}

                {recentVisitors.length < filteredVisitorRecords.length ? (
                  <button
                    type="button"
                    className="visitor-show-more-button"
                    onClick={() => setVisitorRowsShown((current) => current + 25)}
                  >
                    Show 25 more
                  </button>
                ) : null}
              </div>
            </div>
          </section>

          <section className="container pb-4">
            <div className="dashboard-grid dashboard-grid-secondary">
              <div className="dashboard-card inbox-card">
                <div className="card-head">
                  <div>
                    <p className="dashboard-eyebrow mb-1">Inbox</p>
                    <h2 className="h4 fw-bold mb-1">Recent contact messages</h2>
                    <p className="text-muted mb-0">
                      Newest submissions first, with direct reply links.
                    </p>
                  </div>
                  <ReportButton
                    onClick={downloadMessagesCsv}
                    disabled={!contacts.length}
                  />
                </div>

                {recentMessages.length ? (
                  <div className="message-list">
                    {recentMessages.map((contact) => (
                      <article key={contact._id} className="message-card">
                        <div className="message-card-head">
                          <div>
                            <h3 className="h6 fw-bold mb-1">
                              {contact.subject}
                            </h3>
                            <div className="message-meta">
                              <span>{contact.name}</span>
                              <span>{contact.email}</span>
                              <span>
                                {formatDateTime(
                                  contact.createdAt || contact.timestamp,
                                )}
                              </span>
                            </div>
                          </div>
                          <a
                            className="btn btn-sm btn-outline-dark"
                            href={`mailto:${contact.email}?subject=Re: ${encodeURIComponent(
                              contact.subject,
                            )}`}
                          >
                            Reply
                          </a>
                        </div>
                        <p className="message-body mb-0">
                          {truncateText(contact.message, 220)}
                        </p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="empty-panel">No contact messages yet.</div>
                )}
              </div>

              <div className="dashboard-card">
                <div className="card-head">
                  <div>
                    <p className="dashboard-eyebrow mb-1">Conversation</p>
                    <h2 className="h4 fw-bold mb-1">Recent article comments</h2>
                    <p className="text-muted mb-0">
                      Latest comments across all published article pages.
                    </p>
                  </div>
                  <ReportButton
                    onClick={downloadArticleCommentsCsv}
                    disabled={!recentArticleComments.length}
                  />
                </div>

                {recentArticleComments.length ? (
                  <div className="message-list">
                    {recentArticleComments.map((comment) => (
                      <article key={comment.id} className="message-card">
                        <div className="message-card-head">
                          <div>
                            <h3 className="h6 fw-bold mb-1">
                              <Link
                                to={`/articles/${comment.slug}`}
                                className="dashboard-link"
                              >
                                {getArticleDisplayLabel(comment)}
                              </Link>
                            </h3>
                            <div className="message-meta">
                              <span>{comment.name}</span>
                              <span>{formatDateTime(comment.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                        <p className="message-body mb-0">
                          {truncateText(comment.message, 220)}
                        </p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="empty-panel">
                    Article comments will show up here once readers start
                    responding.
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}
      </>
      */}

      {qrApp && (
        <div className="qr-modal-overlay" onClick={() => setQrApp(null)}>
          <div className="qr-modal-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="qr-modal-close"
              onClick={() => setQrApp(null)}
              aria-label="Close"
            >
              ×
            </button>

            <div className="qr-modal-badge">
              <div className="qr-badge-icon">
                <span className="qr-badge-triangle" />
              </div>
              <div className="qr-badge-text">
                <span className="qr-badge-small">GET IT ON</span>
                <span className="qr-badge-big">Google Play</span>
              </div>
            </div>

            <div className="qr-modal-body">
              {qrGenerating ? (
                <div className="qr-placeholder">Generating QR code...</div>
              ) : qrDataUrl ? (
                <img src={qrDataUrl} alt={`QR code for ${qrApp.name}`} className="qr-image" />
              ) : (
                <div className="qr-placeholder">Failed to generate QR code.</div>
              )}
            </div>

            <p className="qr-modal-app-name">{qrApp.name}</p>

            <div className="qr-modal-actions">
              <button
                type="button"
                className="btn btn-success"
                onClick={() => downloadBrandedQr(qrApp)}
                disabled={!qrDataUrl}
              >
                Download PNG
              </button>
              <a
                href={qrApp.playStoreLink}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-dark"
              >
                Open Play Store
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
